import { Injectable } from '@angular/core';

import { OidcSecurityService } from 'angular-auth-oidc-client';
import { DateTime } from 'luxon';
import { map, Observable, of } from 'rxjs';

import { environment } from 'environments/environment';

import { asBoolean } from '../utils/conversions';
import { Role, UserProfile } from './auth.types';

@Injectable({ providedIn: 'root' })
export class AuthService extends OidcSecurityService {
  get userProfile$(): Observable<UserProfile> {
    return this.userData$.pipe(
      map((result) => result?.userData),
      map(
        (userData): UserProfile =>
          userData === null
            ? null
            : {
                ...userData,
                auth_time: DateTime.fromSeconds(userData.auth_time),
                is_cms_user: asBoolean(userData.is_cms_user),
                role: [].concat(userData.role).map((r: string) => Role[r]),
                allSitesEnabled: asBoolean(userData.allSitesEnabled),
                sites: [].concat(userData.sites).map((s) => s),
                initials: `${userData.first_name[0] ?? ''}${userData.last_name[0] ?? ''}`,
                avatar_color: this.getAvatarColor(userData.email),
              },
      ),
    );
  }

  get userRoles$(): Observable<Role[]> {
    return this.userProfile$.pipe(map((p) => (p === null ? null : p.role)));
  }

  public userHasRole(roles: Role | Role[]): Observable<boolean> {
    const requiredRoles: Role[] = [].concat(roles);

    if (roles.length === 0) {
      return of(true);
    }

    return this.userRoles$.pipe(
      map((userRoles) =>
        userRoles !== null && (environment.production || !userRoles.includes(Role.SuperAdmin))
          ? userRoles.some((role) => requiredRoles.includes(role))
          : true,
      ),
    );
  }

  public getAvatarColor(email): string {
    const colors = [
      'red-400',
      'orange-400',
      'amber-400',
      'yellow-300',
      'lime-300',
      'lime-600',
      'green-300',
      'green-700',
      'teal-500',
      'cyan-300',
      'cyan-500',
      'sky-400',
      'blue-300',
      'blue-500',
      'indigo-300',
      'indigo-500',
      'violet-300',
      'violet-500',
      'purple-400',
      'fucshia-300',
      'fucshia-500',
      'pink-300',
      'pink-500',
      'rose-400',
    ];

    const hash = this.hashString(email);
    return colors[hash % colors.length];
  }

  private hashString(value: string): number {
    return Math.abs(
      value.split('').reduce(function (hash, char) {
        hash = (hash << 5) - hash + char.charCodeAt(0);
        return hash & hash;
      }, 0),
    );
  }
}
